import {
  getInterventionOccurrences,
  getInterventionTypeLabel,
  getInterventionMethodLabel,
} from "./timelineOverview";

export const SEVERITY_LEVELS = [
  { key: "sd0", label: "SD0", rank: 0 },
  { key: "sd1", label: "SD1", rank: 1 },
  { key: "sd2", label: "SD2", rank: 2 },
  { key: "sd3", label: "SD3", rank: 3 },
];

const DEFAULT_TOTALS = {
  sd0: 0,
  sd1: 0,
  sd2: 0,
  sd3: 0,
};

const INJECTION_KEYWORDS = [
  "injection",
  "gavage",
  "implant",
  "biopsy",
  "blood sampling",
  "csf",
  "microstimulation",
  "electrophysiology",
  "electromyography",
];

const countSeverityRow = (row = {}) =>
  Object.values(row).reduce((sum, v) => sum + (parseInt(v, 10) || 0), 0);

export const getSeverityTotals = (experiment) => {
  const distribution = experiment?.severityDistribution || {};

  return SEVERITY_LEVELS.reduce((acc, level) => {
    acc[level.key] = countSeverityRow(distribution[level.key]);
    return acc;
  }, { ...DEFAULT_TOTALS });
};

export const getMaxDeclaredSeverity = (experiment) => {
  const totals = getSeverityTotals(experiment);
  const level = [...SEVERITY_LEVELS]
    .reverse()
    .find((item) => totals[item.key] > 0);

  return level || null;
};

const normalize = (value) => String(value || "").trim().toLowerCase();

const getInterventionSignals = (intervention) => {
  const type = normalize(getInterventionTypeLabel(intervention));
  const method = normalize(getInterventionMethodLabel(intervention));
  const combined = `${type} ${method}`;

  const occurrences = getInterventionOccurrences(intervention);
  const repeated =
    intervention?.repeatedIntervention &&
    intervention.repeatedIntervention !== "no" &&
    occurrences.length > 1;

  return {
    occurrences,
    repeated,
    isSurgery: combined.includes("surgery"),
    isTumor: combined.includes("tumor"),
    isTerminal:
      combined.includes("euthanasia") ||
      combined.includes("terminal intervention") ||
      combined.includes("perfusion"),
    isInvasive:
      combined.includes("surgery") ||
      combined.includes("tumor") ||
      combined.includes("blood sampling") ||
      combined.includes("biopsy") ||
      combined.includes("collection") ||
      combined.includes("terminal intervention") ||
      INJECTION_KEYWORDS.some((keyword) => combined.includes(keyword)),
  };
};

export const getExperimentSeverityWarnings = (experiment) => {
  const warnings = [];
  const interventions = experiment?.interventions || [];
  const maxSeverity = getMaxDeclaredSeverity(experiment);
  const declaredDuration = parseInt(experiment?.experimentDuration || "0", 10) || 0;
  const justification = String(experiment?.severityJustification || "").trim();
  const severityTotal = Object.values(getSeverityTotals(experiment)).reduce((sum, value) => sum + value, 0);

  const signals = interventions.map(getInterventionSignals);
  const hasSurgery = signals.some((signal) => signal.isSurgery);
  const hasTumor = signals.some((signal) => signal.isTumor);
  const hasRepeatedInvasive = signals.some((signal) => signal.isInvasive && signal.repeated);
  const hasTerminal = signals.some((signal) => signal.isTerminal);

  if (interventions.length > 0 && severityTotal === 0) {
    warnings.push({
      code: "missing-distribution",
      severity: "warning",
      message:
        "No animals have been distributed across severity degrees yet. Please fill in the Section 36 table before export.",
    });
  }

  if (hasSurgery && (!maxSeverity || maxSeverity.rank === 0)) {
    warnings.push({
      code: "surgery-sd0",
      severity: "warning",
      message:
        "This experiment includes surgery, but the declared severity is currently SD0 only. Please verify or justify this choice.",
    });
  }

  if (hasTumor && maxSeverity && maxSeverity.rank <= 1) {
    warnings.push({
      code: "tumor-low-severity",
      severity: "warning",
      message:
        "This experiment includes tumor induction, but the declared maximum severity is SD0-SD1. Please confirm that this reflects the expected burden.",
    });
  }

  if (hasRepeatedInvasive && (!maxSeverity || maxSeverity.rank === 0)) {
    warnings.push({
      code: "repeated-invasive-sd0",
      severity: "warning",
      message:
        "Repeated invasive procedures were detected, but the declared severity is SD0 only. Please review the severity classification.",
    });
  }

  if (hasSurgery) {
    const surgeryWithoutRecovery = signals.some((signal) => {
      if (!signal.isSurgery || signal.occurrences.length === 0) return false;
      const lastProcedureDay = Math.max(...signal.occurrences);
      return declaredDuration > 0 && declaredDuration <= lastProcedureDay;
    });

    if (surgeryWithoutRecovery) {
      warnings.push({
        code: "surgery-no-recovery",
        severity: "warning",
        message:
          "A surgery is scheduled on the last declared day of the experiment. Add recovery time or verify the timeline duration.",
      });
    }
  }

  if ((hasSurgery || hasTumor || hasTerminal) && !justification) {
    warnings.push({
      code: "missing-justification",
      severity: "info",
      message:
        "Add a short severity justification note if the classification could look unusual to a reviewer.",
    });
  }

  return warnings;
};
